import React, { useMemo } from 'react';
import useKeyboardFormAnimation from 'src/presentation/hooks/useKeyboardFormAnimation';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import { Text, TouchableOpacity, View, StyleSheet, Animated, ScrollView } from 'react-native';
import type { ImageStyle } from 'react-native';
import type { RouteProp } from '@react-navigation/native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Heading from 'src/presentation/components/atoms/Heading';
import RegisterForm from 'src/presentation/components/organisms/forms/RegisterForm';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useFormStore } from 'src/domain/stores/useFormStore';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import Images from 'src/core/utils/Images';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Constants from 'src/core/utils/Constants';

function RegistrationFormScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenRegistrationForm>>();
	const eventStore = useEventStore();
	const formStore = useFormStore();
	const insets = useSafeAreaInsets();
	const { keyboardHeight, imageHeight } = useKeyboardFormAnimation();
	const contentWidth = useContentWidth();

	const sizedStyles = useMemo((): { img: ImageStyle } => ({ img: { width: contentWidth } }), [contentWidth]);

	const onRegister = (routeName: string) => {
		if (routeName === Constants.screenCustom) {
			navigation.goBack();
		} else if (useAuthenticationStore.getState().isLoggedin) {
			// Read the current login status from the store, not from the render closure: onRegister draait synchroon
			// ná register() maar vóór de re-render, dus `authStore.isLoggedin` would still have the old (stale) value
			// here -> the reset never fires and the user stays stuck on the registration screen instead of returning
			// to the (now logged-in) settings tab.
			setTimeout(() => {
				navigation.reset({
					index: 0,
					routes: [{ name: Constants.screenIndex }],
				});
			}, 0);
		}
	};

	const onGoToLogin = () => {
		navigation.goBack();
		navigation.navigate(Constants.screenLoginForm);
	};

	const onForgotPassword = () => {
		navigation.goBack();
		navigation.navigate(Constants.screenForgotPassword);
	};

	return (
		<View style={styles.root}>
			{formStore.loginFromForm ? (
				<NavBar
					backBtn={true}
					bgImage={eventStore.activeTheme.header_bg_image}
					bgcolor={eventStore.activeTheme.header_bg_color}
					color={eventStore.activeTheme.header_text_color}
					editBtn={false}
					statusBarStyle={eventStore.activeTheme.statusbar_color}
					title={Translations.titleRegister}
				/>
			) : (
				<NavBar
					backBtn={true}
					bgcolor={Colors.primary}
					centerTitle={true}
					color={Colors.black}
					editBtn={false}
					title={Translations.titleRegister}
				/>
			)}
			<ScrollView
				contentContainerStyle={{ paddingBottom: insets.bottom + Dimens.padding16 }}
				keyboardShouldPersistTaps="always"
				style={styles.scroll}
			>
				<Animated.View style={{ paddingBottom: keyboardHeight }}>
					{(route?.name as string) === Constants.screenProfileRegistrationPage ? null : (
						<Animated.Image
							resizeMode="cover"
							source={Images.loginHeader}
							style={[styles.img, sizedStyles.img, { height: imageHeight }]}
						/>
					)}
					<View style={styles.contentContainer}>
						<Heading styles={styles.title} text={Translations.titleRegister} />
					</View>
					<RegisterForm
						onForgotPassword={onForgotPassword}
						onGoToLogin={onGoToLogin}
						onRegister={(routeName: string) => onRegister(routeName)}
					/>
					{(route?.name as string) === Constants.screenProfileRegistrationPage ? null : (
						<View style={styles.registerContainer}>
							<Text style={styles.textLinkHelper}>{Translations.textHaveAccount}</Text>
							<TouchableOpacity
								onPress={() => {
									navigation.goBack();
									navigation.navigate(Constants.screenLoginForm);
								}}
							>
								<Text style={styles.textLink}>{Translations.buttonLogin}</Text>
							</TouchableOpacity>
						</View>
					)}
				</Animated.View>
			</ScrollView>
		</View>
	);
}

export default RegistrationFormScreen;

const styles = StyleSheet.create({
	root: {
		flex: 1,
	},
	scroll: {
		backgroundColor: Colors.white,
	},
	img: {
		height: 200,
	},
	textLinkHelper: {
		color: Colors.lightGrey,
	},
	textLink: {
		color: Colors.black,
		fontFamily: Fonts.montserratBold,
	},
	contentContainer: {
		paddingLeft: Dimens.padding20,
		paddingRight: Dimens.padding20,
		paddingTop: Dimens.padding20,
	},
	registerContainer: {
		flexDirection: 'row',
		justifyContent: 'center',
		marginBottom: Dimens.margin20,
	},
	title: {},
});
